import type * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Copy, KeyRound, Plus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { apiErrorMessage } from "@/lib/api";
import { formatRelativeTime } from "@/lib/utils";
import { createPostbackToken, listPostbackTokens, revokePostbackToken } from "@/api/cpa";

const POSTBACK_URL_TEMPLATE =
    "https://api.landingmanager.uk/v1/postback/{token}?lead_ref=...&type=ftd&amount=100.50&external_id=...";

/**
 * Postback-token management for one advertiser. The token is the capability
 * the operator hands to the advertiser's tech team — their server calls the
 * postback URL whenever a lead deposits. Revocation is immediate.
 */
export function PostbackTokensCard({ advertiserId }: { advertiserId: number }): React.ReactNode {
    const queryClient = useQueryClient();
    const confirm = useConfirm();

    const tokensQuery = useQuery({
        queryKey: ["postback-tokens", advertiserId],
        queryFn: () => listPostbackTokens(advertiserId),
    });

    const refresh = (): void => {
        queryClient.invalidateQueries({ queryKey: ["postback-tokens", advertiserId] });
    };
    const createMut = useMutation({
        mutationFn: () => createPostbackToken(advertiserId),
        onSuccess: () => {
            toast.success("Token created — copy it and send it to the advertiser");
            refresh();
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Couldn't create the token")),
    });
    const revokeMut = useMutation({
        mutationFn: (tokenId: number) => revokePostbackToken(advertiserId, tokenId),
        onSuccess: () => {
            toast.success("Token revoked — calls with it are rejected from now on");
            refresh();
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Couldn't revoke the token")),
    });

    const copy = (text: string, what: string): void => {
        navigator.clipboard.writeText(text).then(
            () => toast.success(`${what} copied`),
            () => toast.error("Couldn't copy"),
        );
    };

    const tokens = tokensQuery.data ?? [];

    return (
        <div className="rounded-md border border-border bg-card p-4" data-testid="postback-tokens-card">
            <div className="flex items-center justify-between">
                <div>
                    <div className="flex items-center gap-2 text-sm font-medium">
                        <KeyRound className="h-4 w-4" /> Postback tokens
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                        The advertiser's server reports deposits by calling this URL with their token:
                    </p>
                </div>
                <Button
                    size="sm"
                    variant="outline"
                    onClick={() => createMut.mutate()}
                    disabled={createMut.isPending}
                    data-testid="postback-token-new"
                >
                    {createMut.isPending ? <Spinner size="sm" /> : <Plus className="h-3.5 w-3.5" />} New token
                </Button>
            </div>

            <code className="mt-2 block overflow-x-auto whitespace-nowrap rounded bg-muted/40 px-2 py-1.5 font-mono text-[11px] text-muted-foreground">
                {POSTBACK_URL_TEMPLATE}
            </code>

            {tokensQuery.isLoading ? (
                <div className="flex justify-center py-6">
                    <Spinner />
                </div>
            ) : tokens.length === 0 ? (
                <p className="mt-3 text-xs text-muted-foreground">
                    No tokens yet. Create one and send it to the advertiser's tech team.
                </p>
            ) : (
                <ul className="mt-3 divide-y divide-border/60">
                    {tokens.map((t) => (
                        <li key={t.id} className="flex flex-wrap items-center gap-2 py-2" data-testid={`postback-token-${t.id}`}>
                            <code className="min-w-0 flex-1 truncate rounded bg-muted/40 px-2 py-1 font-mono text-xs">
                                {t.token}
                            </code>
                            <span className="text-[11px] text-muted-foreground">
                                created {formatRelativeTime(t.createdAt)}
                            </span>
                            {t.revokedAt != null ? (
                                <Badge variant="muted">revoked {formatRelativeTime(t.revokedAt)}</Badge>
                            ) : (
                                <>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        title="Copy token"
                                        onClick={() => copy(t.token, "Token")}
                                        data-testid={`postback-token-copy-${t.id}`}
                                    >
                                        <Copy className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() =>
                                            confirm.show({
                                                title: "Revoke this token?",
                                                description:
                                                    "The advertiser's postback calls with it stop working immediately. Create a new token first if they're mid-migration.",
                                                destructive: true,
                                                confirmLabel: "Revoke",
                                                onConfirm: async () => {
                                                    await revokeMut.mutateAsync(t.id);
                                                },
                                            })
                                        }
                                        data-testid={`postback-token-revoke-${t.id}`}
                                    >
                                        Revoke
                                    </Button>
                                </>
                            )}
                        </li>
                    ))}
                </ul>
            )}
            {confirm.dialog}
        </div>
    );
}
